import React from "react";
import {
  Map,
  Ruler,
  Palette,
  Trophy,
  BookOpen,
  FlaskConical,
  Camera,
  Presentation,
  Star,
  Library,
  GraduationCap,
  Layout,
  Brush,
  School,
  ExternalLink,
} from "lucide-react";
import { useQuote } from "../QuoteDialog";

const HALLWAY =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/wc2zq3i0_StockCake-Busy_school_hallway-1333185-medium.jpg";
const ATHLETIC =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/gtwh0teu_School-Hallway-Values-Posters-Created-with-a-School-Poster-Maker.webp";
const GRAD =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/46k2nwlq_IMG_6572.jpg";
const CLASSROOM =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/a7p1pl3p_Students-Using-Posters-Created-with-a-School-Poster-Maker.webp";

const FEATURED = [
  { title: "Posters & banners for school events", img: HALLWAY },
  { title: "Mission, values & spirit banners", img: ATHLETIC },
  { title: "Hallway posters & student displays", img: GRAD },
  { title: "Classroom posters & teaching visuals", img: CLASSROOM },
];

const USES = [
  { icon: Ruler, text: "Blueprints & diagrams for math, engineering, architecture" },
  { icon: Map, text: "Maps for geography classes" },
  { icon: Palette, text: "Artwork for art classes" },
  { icon: Layout, text: "Oversized mockups for design classes" },
  { icon: Camera, text: "Photographs for the school lobby" },
  { icon: Trophy, text: "Signs for hallways & bulletin boards" },
  { icon: Brush, text: "Wallpapers for decorating school walls" },
  { icon: Presentation, text: "Worksheets for science experiments" },
  { icon: Star, text: "Student artwork displays" },
  { icon: BookOpen, text: "Displays for science fairs" },
  { icon: School, text: "Signage for school grounds" },
  { icon: GraduationCap, text: "Classroom wall visuals" },
  { icon: Library, text: "Posters for the library" },
  { icon: FlaskConical, text: "Diagrams for biology & chemistry labs" },
];

export default function PrintersUseCases() {
  const { openQuote } = useQuote();
  return (
    <section
      data-testid="printers-use-cases-section"
      className="bg-[#F4F6F4] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
            Use cases
          </div>
          <h2
            data-testid="printers-use-cases-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
          >
            What Your School Can Create with{" "}
            <span className="text-teal">Print Shop Pro</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Bring ideas to life across your entire school with large-format printing:
          </p>
        </div>

        {/* Featured photo cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6">
          {FEATURED.map((f, i) => (
            <div
              key={f.title}
              data-testid={`use-case-featured-${i}`}
              className="group relative overflow-hidden rounded-2xl bg-slate-200 min-h-[220px] sm:min-h-[260px]"
            >
              <img
                src={f.img}
                alt={f.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/40 to-transparent" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-teal mb-1.5">
                  In-house print
                </span>
                <h3 className="font-serif text-base sm:text-lg lg:text-xl font-bold text-white leading-snug">
                  {f.title}
                </h3>
              </div>
            </div>
          ))}
        </div>

        {/* Icon tiles for the rest */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {USES.map((u, i) => {
            const Icon = u.icon;
            return (
              <li
                key={u.text}
                data-testid={`use-case-${i}`}
                className="group flex items-start gap-3 bg-white border border-slate-200 hover:border-teal hover:shadow-md rounded-xl px-4 py-4 transition-all"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-navy text-white flex items-center justify-center group-hover:bg-teal transition-colors">
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="text-sm font-semibold text-navy leading-snug pt-1.5">
                  {u.text}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-16 max-w-3xl mx-auto text-center">
          <p
            data-testid="printers-use-cases-close"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl font-black text-navy leading-tight"
          >
            If your school can imagine it—
            <br className="hidden sm:block" />
            <span className="text-teal">you can print it.</span>
          </p>
          <button
            data-testid="printers-use-cases-quote-btn"
            onClick={openQuote}
            title="Opens in a new tab"
            className="mt-8 group inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#091638] text-white font-bold px-8 py-4 rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
          >
            Request a Quote
            <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
